import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  Group,
  LoadingOverlay,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useMounted } from "@mantine/hooks";
import { IconPencil, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { StyledLaboratory } from "@pages/Patients/Laboratory/Laboratory.styled";

import { openDeleteModal } from "@components/DeleteModal";
import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";

import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { QP } from "@constants/index";

import { useDeleteClinicTreatmentPlan } from "@hooks/mutations/clinic/useDeleteClinicTreatmentPlan";
import { useGetClinicTreatmentPlansDetailed } from "@hooks/queries/clinic/useGetClinicTreatmentPlansDetailed";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { ClinicTreatmentPlan } from "@customTypes/clinic";

import { StyledResourceManagement } from "../../ResourceManagementTabs.styled";

// import { AddTreatmentPlanModal } from "./components/AddTreatmentPlanModal";
import { EditTreatmentPlanModal } from "./components/EditTreatmentPlanModal";


export const TreatmentPlanTab = () => {
  const { t, i18n } = useTranslation();

  // Table column count for skeleton loading states
  const { tableRef, columnCount } = useTableColumnCount();

  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({ searchFilter });


  const [currentTreatmentPlan, setCurrentTreatmentPlan] =
    useState<ClinicTreatmentPlan | null>(null);

  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();

  const [
    addTreatmentPlanModalOpened,
    { open: openAddTreatmentPlanModal, close: closeAddTreatmentPlanModal },
  ] = useDisclosure(false);

  const [
    editTreatmentPlanModalOpened,
    { open: openEditTreatmentPlanModal, close: closeEditTreatmentPlanModal },
  ] = useDisclosure(false);

  const { data, isLoading, isRefetching, fetchStatus, isError, isSuccess } = useGetClinicTreatmentPlansDetailed({
    pagination: {
      page: tablePagination.page,
      pageSize: tablePagination.pageSize,
    },
    search: debounced.toLowerCase(),
  });
  const {
    mutate: deleteTreatmentPlan,
    isLoading: isDeleteTreatmentPlanLoading,
  } = useDeleteClinicTreatmentPlan();


  // Treatment Plan table rows (AdminDashboard pattern)
  const treatmentPlanRows = useMemo(() => {
    return data?.newData?.map(treatmentPlan => (
      <Table.Tr key={treatmentPlan.id}>
        <Table.Td>
          <Text fz={14} fw={500} lh={1.5} style={{
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {treatmentPlan.name}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5} c="dimmed">
            {treatmentPlan.category || "-"}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} lh={1.5}>
            {treatmentPlan.price || 0}
          </Text>
        </Table.Td>
        <Table.Td>
          <Group gap={8} justify="start">
            <Tooltip label={t("edit")} withArrow>
              <PermissionGate permission="resources.edit"><ActionIcon
                variant="outline"
                size="sm"
                onClick={() => {
                  setCurrentTreatmentPlan(treatmentPlan);
                  openEditTreatmentPlanModal();
                }}
              >
                <IconPencil size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
            <Tooltip label={t("delete")} withArrow>
              <PermissionGate permission="resources.delete"><ActionIcon
                variant="outline"
                color="gray"
                size="sm"
                disabled={true}
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                onClick={() => {
                  openDeleteModal({
                    title: t("delete-treatment-plan"),
                    textNode: (
                      <Text size="sm" lh={1.5}>
                        <Trans
                          i18nKey="delete-treatment-plan-confirmation"
                          values={{ planName: treatmentPlan.name }}
                          components={{ bold: <Text span fw={700} /> }}
                        />
                      </Text>
                    ),
                    confirmText: t("delete-treatment-plan"),
                    onConfirm: () => {
                      deleteTreatmentPlan(treatmentPlan.id);
                    },
                  });
                }}>
                <IconTrash size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [data?.newData, i18n.language]);
  useEffect(() => {
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);





  return (
    <StyledLaboratory>
      <StyledResourceManagement>
        <LoadingOverlay
          visible={isRefetching && !!data?.newData?.length && !isMounted}
          zIndex={1000}
          overlayProps={{ blur: 0.1, opacity: 0.6 }}
          loaderProps={{ display: "none" }}
        />

        <div className="header">
          <Group w={"100%"} justify="space-between" align="center">
            <h1>{t("treatment-plans")}</h1>
            <PermissionGate permission="resources.create"><Button
              leftSection={<IconPlus size={16} />}
              onClick={openAddTreatmentPlanModal}>
              {t("add-treatment-plan")}
            </Button></PermissionGate>
          </Group>
        </div>

        <TextInput
          mt={"md"}
          placeholder={t("search-for-a-treatment-plan")}
          leftSection={<IconSearch size={16} />}
          value={searchFilter || ""}
          onChange={event => setSearchFilter(event.currentTarget.value || null)}
          style={{ flex: 1, maxWidth: 400 }}
        />

        <div style={{paddingBottom: "200px"}}>
          <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }}>
            <Table
              ref={tableRef}
              withRowBorders={true}
              mt={"1rem"}
              miw={1280}
              withTableBorder
              highlightOnHover
              highlightOnHoverColor="var(--clr-gray-100)">
              <Table.Thead>
                <Table.Tr bg={"var(--clr-gray-100)"}>
                  <Table.Th>{t("name")}</Table.Th>
                  <Table.Th>{t("category")}</Table.Th>
                  <Table.Th>{t("price")}</Table.Th>
                  <Table.Th>{t("action")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {renderLoadingErrorData({
                  isLoading: isLoading && fetchStatus === "fetching",
                  isError,
                  isSuccess,
                  dataLength: data?.newData?.length || 0,
                  loadingComponent: (
                    <TableSkeletonRows
                      columns={columnCount}
                      skeletonProps={{
                        widths: ["lg", "md", "md", "md"],
                      }}
                    />
                  ),
                  emptyDataComponent: (
                    <TableEmptyRow
                      colSpan={columnCount}
                      message={
                        debounced
                          ? t("empty-results-for", { searchFilter: `"${debounced}"` })
                          : t("no-treatment-plans-available")
                      }
                    />
                  ),
                  errorComponent: (
                    <TableErrorRow
                      colSpan={columnCount}
                      message={t("encountered-error")}
                    />
                  ),
                  dataComponent: treatmentPlanRows,
                })}
              </Table.Tbody>
            </Table>

            <TablePagination
              paginationData={data?.pagination || {}}
              paginatedRows={data?.newData?.length || 0}
              onPageChange={(page) => setTablePagination(prev => ({ ...prev, page }))}
              isLoading={isLoading}
            />
          </ScrollArea>
        </div>



        {/* <AddTreatmentPlanModal
        opened={addTreatmentPlanModalOpened}
        close={closeAddTreatmentPlanModal}
      /> */}

        <EditTreatmentPlanModal
          opened={editTreatmentPlanModalOpened}
          close={closeEditTreatmentPlanModal}
          treatmentPlan={currentTreatmentPlan}
        />
      </StyledResourceManagement>
    </StyledLaboratory>
  );
};
